/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useCallback, useState } from 'react';
import { isObjEmptyOrNull, isStrEmptyOrNull, getErrorFromEndpoint } from '../UtilityFunctions';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { useAuthContext } from '../../contexts/AuthContext';

const useBatch = () => {
  const { token } = useAuthContext();
  const { get } = useAbortController();
  const [batchResp, setBatchResp] = useState<UnknownBatchEndpointResp>(null);
  const [batchErrors, setBatchErrors] = useState('');
  const [batchLoading, setBatchLoading] = useState(false);

  const batchAPI = useCallback(
    async (params: BatchEndpointReq): Promise<UnknownBatchEndpointResp> => {
      if (!token || !params) return null;
      const urlPath = process.env.REACT_APP_BATCH_REQUEST;
      const request = { url: urlPath, data: params, method: 'post' };
      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      const config = get();

      const res = await axiosCall(request, config, headers)
        .then((res) => {
          if (!isObjEmptyOrNull(res)) {
            const err = getErrorFromEndpoint(res);
            if (isStrEmptyOrNull(err)) {
              setBatchResp(res);
              return res;
            } else {
              console.log(err);
              setBatchErrors(err);
              return null;
            }
          }
          return null;
        })
        .catch((e: Error) => {
          console.log('Error using batchAPI in the useBatch hook: ', e);
          setBatchErrors(e.message);
          return null;
        });
      setBatchLoading(false);
      return res;
    },
    [get, token],
  );

  const getBatch = useCallback(
    async (params: BatchEndpointReq) => {
      setBatchResp(null);
      if (!isObjEmptyOrNull(params)) {
        setBatchLoading(true);
        const res = await batchAPI(params);
        return res;
      }
      return null;
    },
    [batchAPI],
  );

  return { batchResp, batchLoading, batchErrors, getBatch };
};

export default useBatch;
